import { useCallback, useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  createBooking,
  getUserSubscriptions,
  listTables,
  ApiError,
  slotConflictDetail,
  type CreateBookingBody,
} from '../../lib/api';
import { Button } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import { SlotConflictBanner } from '../../components/SlotConflictBanner';
import { UserPicker } from '../../components/UserPicker';
import { useToast } from '../../hooks/useToast';
import { todayLocal, formatClock } from '../../lib/time';
import { POLICY, POLICY_MIN_TIME, POLICY_MAX_START_TIME } from '../../lib/policy';
import { windowAtNextFreeTime } from '../../lib/slot-suggestion';
import { TimeSelect } from '../../components/TimeSelect';
import { PS, T } from '../../lib/theme';
import { fullName } from '../../lib/format';
import type {
  AdminLookupUser,
  SlotConflictDetail,
  SubscriptionWithUsage,
  TableEmbed,
} from '../../lib/types';

type PaymentChoice = 'one_time' | 'subscription' | 'credit';

const BOOKING_REJECTION_MESSAGES: Record<string, string> = {
  outside_opening_hours: 'Outside club opening hours',
  maintenance_window: 'Inside a maintenance window',
};

export function NewBookingPage() {
  const navigate = useNavigate();
  const { push } = useToast();

  // Tables for dropdown
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  const tables = useApi<TableEmbed[]>(tablesLoader);

  // User lookup
  const [user, setUser] = useState<AdminLookupUser | null>(null);

  // Subscriptions of the selected user (only fetched once user picked)
  const [subs, setSubs] = useState<SubscriptionWithUsage[]>([]);
  useEffect(() => {
    if (!user) {
      setSubs([]);
      return;
    }
    let cancelled = false;
    void getUserSubscriptions(user.id).then((s) => {
      if (!cancelled) setSubs(s.filter((x) => x.status === 'active'));
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Form fields
  const [tableId, setTableId] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [payment, setPayment] = useState<PaymentChoice>('one_time');
  const [subscriptionId, setSubscriptionId] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState<SlotConflictDetail | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit =
    user !== null && tableId && startTime && endTime && date && !submitting &&
    (payment !== 'subscription' || subscriptionId !== '');

  // The window the API's `next_free_time` would produce, or null when it yields
  // nothing usable (no free time left, or a suggestion too late in the day to
  // fit the duration). Null means the banner is shown without the action rather
  // than with one that can't work — see lib/slot-suggestion.ts.
  const suggestion = conflict
    ? windowAtNextFreeTime(startTime, endTime, conflict.next_free_time)
    : null;

  const useSuggestion = () => {
    if (!suggestion) return;
    setStartTime(suggestion.startTime);
    setEndTime(suggestion.endTime);
    // The panel describes a submit that no longer matches the form; leaving it
    // up would read as "this window is also taken", which we don't know yet.
    setConflict(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !canSubmit) return;
    setSubmitting(true);
    setConflict(null);
    setSubmitError(null);
    try {
      const body: CreateBookingBody = {
        user_id: user.id,
        table_id: Number(tableId),
        date,
        start_time: startTime,
        end_time: endTime,
        payment_type: payment,
        subscription_id: payment === 'subscription' ? Number(subscriptionId) : null,
        payment_id: null,
      };
      const booking = await createBooking(body);
      push(
        'success',
        `Booking #${booking.id} created for ${fullName(user)} on ${booking.date} ${formatClock(booking.start_time)}.`,
      );
      navigate(`/bookings?date=${booking.date}`);
    } catch (err) {
      if (err instanceof ApiError) {
        const mappedMessage = err.code ? BOOKING_REJECTION_MESSAGES[err.code] : undefined;
        if (mappedMessage) {
          setSubmitError(mappedMessage);
          push('error', mappedMessage);
        } else {
          // Since api#81 the API owns this message: every 409 here carries a
          // structured `detail` with its own per-call-site `error` string. Let
          // it through rather than hardcoding one sentence for every 409 code —
          // the hand-written copy stays as the fallback for a body we could not
          // parse (an unreachable API, a proxy's own 409, etc.).
          const detail = slotConflictDetail(err);
          if (detail) {
            // Toast *and* panel: the toast is the attention signal next to the
            // button that was just clicked, the panel carries the detail a toast
            // cannot hold. Same split as NewOneTimeCoachSessionPage.
            setConflict(detail);
            push('error', detail.error);
          } else if (err.status === 409) {
            push('error', 'Slot conflict — that time on that table is already booked.');
          } else {
            push('error', err.message);
          }
        }
      } else {
        push('error', 'Create failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>New booking</h1>
        <p style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
          Create a booking on behalf of a user (walk-in, phone-in, etc.).
        </p>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          background: PS.white,
          padding: 20,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
        }}
      >
        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* UserPicker's input carries its own aria-label, so a plain <div>
              (not <label>) groups the caption text — a <label> here can't be
              associated with the control it wraps. */}
          <div style={T.label}>User</div>
          <UserPicker
            selected={user}
            onSelect={setUser}
            placeholder="Search by name, phone, or email…"
            hint={user ? `Credit: ${user.credit_minutes} min · Active subs: ${subs.length}` : undefined}
            ariaLabel="User"
          />
        </section>

        <Field label="Table">
          <select value={tableId} onChange={(e) => setTableId(e.target.value)} style={selectStyle}>
            <option value="">—</option>
            {(tables.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Date">
          <DatePicker value={date} onChange={setDate} />
        </Field>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Start time">
            <TimeSelect
              value={startTime}
              onChange={setStartTime}
              min={POLICY_MIN_TIME}
              max={POLICY_MAX_START_TIME}
              stepMinutes={POLICY.SLOT_STEP_MINUTES}
              ariaLabel="Start time"
            />
          </Field>
          <Field label="End time">
            <TimeSelect
              value={endTime}
              onChange={setEndTime}
              min={POLICY_MIN_TIME}
              max="24:00"
              includeMax={true}
              stepMinutes={POLICY.SLOT_STEP_MINUTES}
              ariaLabel="End time"
            />
          </Field>
        </div>

        <Field label="Payment type">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <RadioOption
              label="One-time (no payment)"
              checked={payment === 'one_time'}
              onChange={() => setPayment('one_time')}
            />
            <RadioOption
              label={`Subscription (${subs.length} active)`}
              checked={payment === 'subscription'}
              onChange={() => setPayment('subscription')}
              disabled={!user || subs.length === 0}
            />
            <RadioOption
              label={`Credit (${user?.credit_minutes ?? 0} min)`}
              checked={payment === 'credit'}
              onChange={() => setPayment('credit')}
              disabled={!user || (user.credit_minutes ?? 0) === 0}
            />
          </div>
        </Field>

        {payment === 'subscription' && (
          <Field label="Subscription">
            <select
              value={subscriptionId}
              onChange={(e) => setSubscriptionId(e.target.value)}
              style={selectStyle}
            >
              <option value="">—</option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>
                  #{s.id} · {s.plan} · {s.hours_remaining?.toFixed(1) ?? '?'}h left
                </option>
              ))}
            </select>
          </Field>
        )}

        {submitError && (
          <div
            role="alert"
            style={{
              padding: 10,
              borderRadius: 3,
              background: PS.errorBg,
              border: `1px solid ${PS.errorBorder}`,
              color: PS.errorText,
              ...T.ui,
            }}
          >
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create booking'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/bookings')} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </form>

      {conflict && (
        <SlotConflictBanner
          detail={conflict}
          onUseNextFreeTime={suggestion ? useSuggestion : undefined}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={T.label}>{label}</span>
      {children}
    </label>
  );
}

function RadioOption({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input type="radio" checked={checked} onChange={onChange} disabled={disabled} />
      <span style={T.ui}>{label}</span>
    </label>
  );
}

const inputStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
};
const selectStyle: CSSProperties = {
  ...inputStyle,
  background: PS.white,
};
